/**
 * Avatar animé de qui a ENREGISTRÉ la voix (migration 0675).
 *
 * Une voix enregistrée porte le compte qui l'a dite (`speakerId`) ; si ce
 * compte a des sprites (8 images : 0 = bouche fermée, 1..7 = ouverte), son
 * avatar s'affiche en haut à droite pendant la scène et la bouche bouge sur
 * les mots (horodatage Whisper). Jamais pour une voix de synthèse ni pour la
 * voix du navigateur. Tout ici est PUR et ne dépend que du temps : même rendu
 * dans l'app et dans la vidéo rendue image par image.
 */

/** Ce que ces règles lisent d'un document (brut ou validé). */
interface DocLike {
  assets?: readonly unknown[] | null;
  scenes?: readonly unknown[] | null;
  bookends?: Record<string, unknown> | null;
}

interface AudioLike {
  id: string;
  kind: 'audio';
  voice?: string;
  speakerId?: string;
  words?: readonly { startMs: number; endMs: number }[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function audioAssets(doc: DocLike): AudioLike[] {
  return (doc.assets ?? []).filter(
    (a): a is AudioLike => isRecord(a) && a.kind === 'audio' && typeof a.id === 'string',
  );
}

/** Voix enregistrée (pas la synthèse) qui porte le compte de quelqu'un. */
function spokenBy(asset: AudioLike | undefined): string | null {
  if (!asset || asset.voice === 'synthetic') return null;
  return typeof asset.speakerId === 'string' && asset.speakerId ? asset.speakerId : null;
}

/** Voix (ressource) jouée par la narration d'une scène, sinon null. */
export function sceneVoice(doc: DocLike, sceneId: string): AudioLike | null {
  const scene = (doc.scenes ?? []).find((s) => isRecord(s) && s.id === sceneId);
  const narration = isRecord(scene) && isRecord(scene.narration) ? scene.narration : null;
  const id =
    narration && typeof narration.audioAssetId === 'string' ? narration.audioAssetId : null;
  if (!id) return null;
  return audioAssets(doc).find((a) => a.id === id) ?? null;
}

/** Compte qui parle dans cette scène (voix enregistrée), sinon null. */
export function sceneSpeaker(doc: DocLike, sceneId: string): string | null {
  return spokenBy(sceneVoice(doc, sceneId) ?? undefined);
}

/** Comptes qui ont enregistré au moins une voix utilisée, dans l'ordre d'apparition. */
export function documentSpeakers(doc: DocLike): string[] {
  const used = new Set<string>();
  for (const s of doc.scenes ?? []) {
    const n = isRecord(s) && isRecord(s.narration) ? s.narration : null;
    if (n && typeof n.audioAssetId === 'string') used.add(n.audioAssetId);
  }
  for (const v of Object.values(doc.bookends ?? {})) if (typeof v === 'string') used.add(v);
  const out: string[] = [];
  for (const a of audioAssets(doc)) {
    const who = used.has(a.id) ? spokenBy(a) : null;
    if (who && !out.includes(who)) out.push(who);
  }
  return out;
}

/** Nombre d'images d'un avatar : 0 = bouche fermée, 1..7 = bouche ouverte. */
export const SPEAKER_FRAME_COUNT = 8;
/** Durée d'une image de bouche (≈ 11 images par seconde). */
export const MOUTH_FRAME_MS = 90;
/** Ordre des bouches ouvertes : varié pour ne pas « boucler » visiblement. */
const OPEN_SEQUENCE = [3, 5, 2, 6, 4, 7, 1, 5, 3, 6, 2, 4, 7, 1] as const;

/**
 * Image de bouche à l'instant `tMs` de la voix (ms depuis son début).
 * Avec l'horodatage des mots : bouche ouverte pendant un mot, fermée entre
 * deux. Sans horodatage : ouverte tant que la voix joue.
 */
export function mouthFrameAt(
  words: readonly { startMs: number; endMs: number }[] | null | undefined,
  tMs: number,
  playing: boolean,
): number {
  if (!playing || !Number.isFinite(tMs) || tMs < 0) return 0;
  if (words && words.length > 0) {
    const inWord = words.some((w) => tMs >= w.startMs && tMs < w.endMs);
    if (!inWord) return 0;
  }
  const step = Math.floor(tMs / MOUTH_FRAME_MS);
  return OPEN_SEQUENCE[step % OPEN_SEQUENCE.length] ?? 0;
}

/** Ce que `videoVoiceAt` lit du plan d'une vidéo (`VideoPlan`). */
interface ClipLike {
  assetId: string;
  fromMs: number;
  toMs: number;
  atMs: number;
}

/**
 * Voix qui joue à l'instant `ms` d'une vidéo : ressource et instant DANS LE
 * FICHIER (les mots Whisper sont datés depuis le début du fichier), sinon null.
 */
export function videoVoiceAt(
  plan: { clips: readonly ClipLike[] },
  ms: number,
): { assetId: string; fileMs: number } | null {
  for (const c of plan.clips) {
    const len = c.toMs - c.fromMs;
    if (ms >= c.atMs && ms < c.atMs + len)
      return { assetId: c.assetId, fileMs: c.fromMs + (ms - c.atMs) };
  }
  return null;
}
